import React from "react";
import { QRCodeCanvas } from "qrcode.react";
import jsQR from "jsqr";

const CHUNK_SIZE = 64 * 1024; // 64KB

// ======================================================
// QR SCANNER (camera-based, no external libs)
// ======================================================

class QRScanner extends React.Component<{ onScan: (value: string) => void }> {
  videoRef = React.createRef<HTMLVideoElement>();
  canvasRef = React.createRef<HTMLCanvasElement>();
  intervalId: number | null = null;

  async componentDidMount() {
    await this.startCamera();
  }

  componentWillUnmount() {
    this.stopCamera();
  }

  async startCamera() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
        await this.videoRef.current.play();
        console.log("Camera started, video dimensions:", this.videoRef.current.videoWidth, "x", this.videoRef.current.videoHeight);
      }
      this.intervalId = window.setInterval(() => this.scanFrame(), 1000);
    } catch (e) {
      console.error("Camera error", e);
    }
  }

  stopCamera() {
    if (this.intervalId != null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    const video = this.videoRef.current;
    const stream = video?.srcObject as MediaStream | null;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      if (video) video.srcObject = null;
    }
  }

  async scanFrame() {
    const video = this.videoRef.current;
    const canvas = this.canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      console.log("Video not ready:", { video: !!video, canvas: !!canvas, readyState: video?.readyState });
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    console.log("Scanning frame:", imageData.width, "x", imageData.height, "pixels:", imageData.data.length / 4);

    try {
      // jsQR expects RGBA data
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "attemptBoth"
      });
      if (code) {
        console.log("QR Code detected:", code.data);
        this.props.onScan(code.data);
        this.stopCamera();
      } else {
        console.log("No QR code found in frame");
      }
    } catch (e) {
      console.error("QR scan error", e);
    }
  }

  render() {
    return (
      <div>
        <video
          ref={this.videoRef}
          style={{ width: "100%", borderRadius: 8, background: "#000" }}
        />
        <canvas ref={this.canvasRef} style={{ display: "none" }} />
      </div>
    );
  }
}

// ======================================================
// MAIN MODAL (NO ENCRYPTION)
// ======================================================

export class BlobTransferPopup extends React.Component<
  {
    isOpen: boolean;
    onClose: () => void;
    blobToSend?: { blob: Blob; filename: string } | null;
    onBlobReceived?: (blob: Blob) => void;
    onFileReceived?: (file: File) => void;
    mode: "sender" | "receiver" | null;
  },
  {
    localSignal: string;
    remoteSignal: string;
    connectionState: string;
    sendProgress: number;
    receiveProgress: number;
    sendSpeed: number;
    receiveSpeed: number;
    sendETA: number | null;
    receiveETA: number | null;
    scanning: boolean;
  }
> {
  pc: RTCPeerConnection | null = null;
  dataChannel: RTCDataChannel | null = null;

  incomingBuffers: Uint8Array[] = [];
  expectedSize: number | null = null;
  receivedBytes = 0;

  sendStartTime: number | null = null;
  receiveStartTime: number | null = null;

  fileInputRef = React.createRef<HTMLInputElement>();

  constructor(props : any) {
    super(props);
    this.state = {
      localSignal: "",
      remoteSignal: "",
      connectionState: "idle",
      sendProgress: 0,
      receiveProgress: 0,
      sendSpeed: 0,
      receiveSpeed: 0,
      sendETA: null,
      receiveETA: null,
      scanning: false,
    };
  }

  componentDidUpdate(prevProps : any) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.resetState();
    }
    // Uncomment to test importing code without going through WebRTC flow:
    // if(!prevProps.blobToSend && this.props.blobToSend){
    //   this.props.onBlobReceived?.(this.props.blobToSend.blob);
    // }
  }

  resetState() {
    this.cleanup();
    this.incomingBuffers = [];
    this.expectedSize = null;
    this.receivedBytes = 0;
    this.sendStartTime = null;
    this.receiveStartTime = null;

    this.setState({
      localSignal: "",
      remoteSignal: "",
      connectionState: "idle",
      sendProgress: 0,
      receiveProgress: 0,
      sendSpeed: 0,
      receiveSpeed: 0,
      sendETA: null,
      receiveETA: null,
      scanning: false,
    }, () => {
      if(this.props.isOpen) {
        if (this.props.mode === "receiver") {
          this.startAsReceiver();
        } else if (this.props.mode === "sender") {
          this.startAsSender();
        }
      }
    });
  }

  cleanup() {
    if (this.dataChannel) this.dataChannel.close();
    if (this.pc) this.pc.close();
    this.dataChannel = null;
    this.pc = null;
  }

  // ======================================================
  // WebRTC setup
  // ======================================================

  createPeerConnection() {
    if (this.pc) return this.pc;

    this.pc = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    this.pc.onicecandidate = (event) => {
      if (!event.candidate && this.pc?.localDescription) {
        const payload = { sdp: this.pc.localDescription };
        const signalString = JSON.stringify(payload);
        console.log("Generated signal:", signalString.length, "characters");
        this.setState({ localSignal: signalString });
      }
    };

    this.pc.onconnectionstatechange = () => {
      console.log("Connection state changed:", this.pc?.connectionState);
      this.setState({ connectionState: this.pc!.connectionState });

      if(this.pc?.connectionState === "disconnected" || this.pc?.connectionState === "closed"){
        this.cleanup();
        this.props.onClose();
      }
    };

    this.pc.ondatachannel = (event) => {
      this.setupSenderChannel(event.channel);
    };

    return this.pc;
  }

  setupSenderChannel(channel: RTCDataChannel) {
    this.dataChannel = channel;

    channel.onopen = async () => {
      this.setState({ connectionState: "connected" });
      if (this.props.blobToSend) {
        this.sendStartTime = performance.now();
        await this.sendBlob(this.props.blobToSend.blob, channel);
      }
    };

    channel.onclose = () => this.setState({ connectionState: "closed" });
    channel.onerror = () => this.setState({ connectionState: "error" });
  }

  setupReceiverChannel(channel: RTCDataChannel) {
    this.dataChannel = channel;

    channel.onopen = () => {
      this.setState({ connectionState: "connected" });
      this.receiveStartTime = performance.now();
    };

    channel.onmessage = async (event) => {
      if (typeof event.data === "string") {
        try {
          const meta = JSON.parse(event.data);
          if (meta.type === "meta") {
            this.expectedSize = meta.size;
            this.incomingBuffers = [];
            this.receivedBytes = 0;
            this.setState({
              receiveProgress: 0,
              receiveSpeed: 0,
              receiveETA: null,
            });
          }
        } catch {}
        return;
      }

      const buffer =
        event.data instanceof Blob
          ? await event.data.arrayBuffer()
          : event.data;

      this.incomingBuffers.push(new Uint8Array(buffer));
      this.receivedBytes += buffer.byteLength;

      if (this.expectedSize && this.receiveStartTime) {
        const elapsed = (performance.now() - this.receiveStartTime) / 1000;
        const speed = this.receivedBytes / Math.max(elapsed, 0.001);
        const remaining = this.expectedSize - this.receivedBytes;
        const eta = remaining > 0 ? remaining / speed : 0;

        this.setState({
          receiveProgress: (this.receivedBytes / this.expectedSize) * 100,
          receiveSpeed: speed,
          receiveETA: eta,
        });
      }

      if (this.expectedSize && this.receivedBytes >= this.expectedSize) {
        const full = this.concatBuffers(this.incomingBuffers);
        const blob = new Blob([full]);
        this.props.onBlobReceived?.(blob);
        channel.close();
        this.cleanup();
        this.props.onClose();
      }
    };
  }

  concatBuffers(chunks: Uint8Array[]) {
    const total = chunks.reduce((sum, c) => sum + c.length, 0);
    const result = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  }

  // ======================================================
  // Sending (no encryption)
  // ======================================================

  async sendBlob(blob: Blob, channel: RTCDataChannel) {
    const buffer = await blob.arrayBuffer();
    const size = buffer.byteLength;

    channel.send(JSON.stringify({ type: "meta", size }));

    let offset = 0;
    while (offset < size) {
      const slice = buffer.slice(offset, offset + CHUNK_SIZE);
      channel.send(slice);
      offset += CHUNK_SIZE;

      if (this.sendStartTime) {
        const elapsed = (performance.now() - this.sendStartTime) / 1000;
        const speed = offset / Math.max(elapsed, 0.001);
        const remaining = size - offset;
        const eta = remaining > 0 ? remaining / speed : 0;

        this.setState({
          sendProgress: (offset / size) * 100,
          sendSpeed: speed,
          sendETA: eta,
        });
      }
    }

    channel.close();
    this.setState({ connectionState: "completed" });
    // this.cleanup();
    // this.props.onClose();
  }

  // ======================================================
  // Role selection
  // ======================================================

  async startAsSender() {
    this.setState({
      connectionState: "idle",
      sendProgress: 0,
      sendSpeed: 0,
      sendETA: null,
    });
    this.createPeerConnection();
  }

  async startAsReceiver() {
    this.setState({
      connectionState: "idle",
      receiveProgress: 0,
      receiveSpeed: 0,
      receiveETA: null,
    });

    const pc = this.createPeerConnection();
    const channel = pc.createDataChannel("file-transfer");
    this.setupReceiverChannel(channel);

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
  }

  async applyRemoteSignal() {
    if (!this.pc || !this.state.remoteSignal) return;

    const payload = JSON.parse(this.state.remoteSignal);
    const { sdp } = payload;

    await this.pc.setRemoteDescription(sdp);

    if (this.props.mode === "sender") {
      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      this.setState({ connectionState: "connecting" });
    }
  }

  // ======================================================
  // Helpers
  // ======================================================

  handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && this.props.onFileReceived) {
      this.props.onFileReceived(file);
    }
  };

  formatSpeed(bytesPerSec: number) {
    if (!bytesPerSec) return "-";
    const kb = bytesPerSec / 1024;
    const mb = kb / 1024;
    if (mb >= 1) return `${mb.toFixed(2)} MB/s`;
    if (kb >= 1) return `${kb.toFixed(2)} KB/s`;
    return `${bytesPerSec.toFixed(0)} B/s`;
  }

  formatETA(seconds: number | null) {
    if (seconds == null) return "-";
    if (seconds < 1) return "<1s";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    if (m === 0) return `${s}s`;
    return `${m}m ${s}s`;
  }

  // ======================================================
  // Render
  // ======================================================

  render() {
    if (!this.props.isOpen) return null;

    const {
      localSignal,
      remoteSignal,
      connectionState,
      sendProgress,
      receiveProgress,
      sendSpeed,
      receiveSpeed,
      sendETA,
      receiveETA,
      scanning,
    } = this.state;

    const title = this.props.mode === "sender" ? "Exporting Training" : "Importing Training";

    var instructions = "";
    if (this.props.mode === "sender") {
      if(connectionState === "connected"){
        instructions = "Your training is being sent! Please wait until the transfer is complete before closing this window.";
      }else if(connectionState === "connecting"){
        instructions = "Have the importing app scan your QR code. Please wait...";
      }else if(connectionState === "idle"){
        instructions = "To export the training, initiate the connection with the importing app by scanning its QR code, or you can choose to download the file to your device.";
      }
    } else if (this.props.mode === "receiver") {
      if(connectionState === "connected"){
        instructions = "Your training is being received! Please wait until the transfer is complete before closing this window.";
      }else if(connectionState === "connecting"){
        instructions = "Choose Scan QR Code to scan the exporting app's QR code. This completes the connection between the two devices.";
      }else if(connectionState === "idle"){
        instructions = "To receive the training, have the exporting app scan your QR code, or choose a file currently on your device.";
      }
    }

    return (
      <>
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "var(--theme)",
              color: "var(--theme-font)",
              borderRadius: 8,
              padding: 16,
              width: 680,
              maxWidth: "95%",
              maxHeight: "95%",
              overflow: "auto",
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
              border: "1px solid var(--theme-border)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <h2 style={{ color: "var(--theme-font)" }}>{title}</h2>
              <button
                onClick={() => {
                  this.cleanup();
                  this.props.onClose();
                }}
                style={{
                  background: "transparent",
                  color: "var(--theme-font)",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: 4,
                }}
              >
                ✕
              </button>
            </div>

            {/* Hidden file input */}
            <input
              ref={this.fileInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={this.handleUploadFile}
            />

            {/* Active mode */}
            {this.props.mode && (
              <>
                <p style={{fontSize: ".9em"}}>
                  <strong>Connection Status:</strong> {connectionState}
                </p>
                <p style={{fontSize: ".8em"}}>
                  {instructions}
                </p>

                <hr style={{ borderColor: "var(--theme-border)" }} />

                {connectionState !== "connected" && (
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    {this.props.mode === "sender" && (
                      <div style={{ width: "100%" }}>
                        {connectionState === "idle" && (
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", minWidth: 260, gap: 8 }}>
                            {this.props.blobToSend && (
                              <button
                                style={{
                                  background: "var(--primary)",
                                  color: "white",
                                  border: "none",
                                  padding: "8px 16px",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  const { blob, filename } = this.props.blobToSend || {};
                                  if (blob) {
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download = filename || "file";
                                    a.click();
                                    URL.revokeObjectURL(url);
                                  }
                                }}
                              >
                                Download File
                              </button>
                            )}
                            <button
                              style={{
                                background: "var(--primary)",
                                color: "white",
                                border: "none",
                                padding: "8px 16px",
                                borderRadius: 4,
                                cursor: "pointer",
                              }}
                              onClick={() => this.setState({ scanning: true })}
                            >
                              Scan QR Code
                            </button>
                          </div>
                        )}
                        {connectionState === "connecting" && (
                          <div style={{ marginBottom: 8 }}>
                            {/* <h3 style={{ color: "var(--theme-font)", marginBottom: 8 }}>Your QR Code</h3> */}
                            <QRCodeCanvas 
                              value={localSignal} 
                              size={360} 
                              level="M"
                              includeMargin={true}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {this.props.mode === "receiver" && (
                      <>
                          {connectionState === "idle" && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, width: "100%" }}>
                              <div style={{ minWidth: 260, width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", alignItems: "center" }}>
                                {/* <h3 style={{ color: "var(--theme-font)", marginBottom: 8 }}>Your QR Code</h3> */}
                                <QRCodeCanvas 
                                  value={localSignal} 
                                  size={360} 
                                  level="M"
                                  includeMargin={true}
                                />
                              </div>
                              <button
                                style={{
                                  background: "var(--primary)",
                                  color: "white",
                                  border: "none",
                                  padding: "8px 16px",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  minWidth: 220,
                                }}
                                onClick={() => this.fileInputRef.current?.click()}
                              >
                                Choose File to Import
                              </button>
                            </div>
                        )}
                        {connectionState === "connecting" && (
                            <div style={{ display: "flex", width: "100%", justifyContent: "center", alignItems: "center" }}>
                              <button
                                style={{
                                  background: "var(--primary)",
                                  color: "white",
                                  border: "none",
                                  padding: "8px 16px",
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  margin: "0 auto",
                                }}
                                onClick={() => this.setState({ scanning: true })}
                              >
                                Scan QR Code
                              </button>
                            </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Progress */}
                {connectionState === "connected" && (
                  <>
                    {this.props.mode === "sender" && (
                      <div style={{ marginTop: 8 }}>
                        <h3 style={{ color: "var(--theme-font)" }}>Send Progress</h3>
                        <progress
                          value={sendProgress}
                      max={100}
                      style={{
                        width: "100%",
                        height: "8px",
                        borderRadius: "4px",
                        background: "var(--theme-background)",
                        border: "1px solid var(--theme-border)",
                      }}
                    />
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      {sendProgress.toFixed(1)}% •{" "}
                      {this.formatSpeed(sendSpeed)} • ETA{" "}
                      {this.formatETA(sendETA)}
                    </div>
                  </div>
                  )}

                  {this.props.mode === "receiver"  && (
                    <div style={{ marginTop: 8 }}>
                      <h3 style={{ color: "var(--theme-font)" }}>Receive Progress</h3>
                      <progress
                        value={receiveProgress}
                        max={100}
                        style={{
                          width: "100%",
                          height: "8px",
                          borderRadius: "4px",
                          background: "var(--theme-background)",
                          border: "1px solid var(--theme-border)",
                        }}
                      />
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        {receiveProgress.toFixed(1)}% •{" "}
                        {this.formatSpeed(receiveSpeed)} • ETA{" "}
                        {this.formatETA(receiveETA)}
                      </div>
                    </div>
                  )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* QR Scanner Overlay */}
        {scanning && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.85)",
              zIndex: 10000,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
          >
            <h2 style={{ color: "white", marginBottom: 12 }}>Scan QR Code</h2>
            <div style={{ width: "90%", maxWidth: 420 }}>
              <QRScanner
                onScan={(value) => {
                  this.setState({ remoteSignal: value, scanning: false }, () => this.applyRemoteSignal());
                }}
              />
            </div>
            <button
              style={{
                marginTop: 16,
                background: "var(--theme)",
                color: "var(--theme-font)",
                border: "1px solid var(--theme-border)",
                padding: "8px 16px",
                borderRadius: 4,
                cursor: "pointer",
              }}
              onClick={() => this.setState({ scanning: false })}
            >
              Cancel
            </button>
          </div>
        )}
      </>
    );
  }
}